import React, { useState } from 'react';
import { Heart, Gift, Calendar, CreditCard, Check, Sparkles, TreePine, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import Header from '../Header';
import Footer from '../Footer';
import { API_URL } from '../../lib/api';

const DonatePage = () => {
  const [donationType, setDonationType] = useState('one-time'); // 'one-time' or 'monthly'
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [loading, setLoading] = useState(false);

  const amounts = [5, 10, 25, 50, 100];

  const handleDonate = async () => {
    const amount = selectedAmount || parseFloat(customAmount);
    
    if (!amount || amount < 1) {
      toast.error('Please select or enter a donation amount');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/create-donation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: amount,
          recurring: donationType === 'monthly'
        })
      });

      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error('Failed to create donation session');
      }
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    }
    setLoading(false);
  };

  const impactItems = [
    { amount: '£5', impact: 'Provides a Bible storybook to one child' },
    { amount: '£10', impact: 'Sponsors a child for one month of reading materials' },
    { amount: '£25', impact: 'Funds a small group Bible study session' },
    { amount: '£50', impact: 'Supports a readathon event for 10 children' },
    { amount: '£100', impact: 'Helps publish a new Christian children\'s book' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-emerald-50 to-sky-50">
      <Header />
      
      <main className="pt-24 pb-16">
        {/* Hero Section */}
        <section className="max-w-4xl mx-auto px-4 text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-coral-50 text-coral-600 px-4 py-2 rounded-full mb-6">
            <Heart className="w-4 h-4" />
            <span className="text-sm font-medium">Support Our Mission</span>
          </div>
          
          <h1 className="text-4xl sm:text-5xl font-serif font-bold text-slate-800 mb-4">
            Help Children Discover
            <span className="text-emerald-600"> God's Love</span>
          </h1>
          
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Your generosity helps us create and distribute faith-building resources to children 
            who need them most. Every donation makes a difference.
          </p>
        </section>

        {/* Donation Card */}
        <section className="max-w-2xl mx-auto px-4">
          <div className="bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden">
            {/* Donation Type Toggle */}
            <div className="flex border-b border-slate-100">
              <button
                onClick={() => setDonationType('one-time')}
                className={`flex-1 py-4 px-6 font-medium transition-colors flex items-center justify-center gap-2 ${
                  donationType === 'one-time'
                    ? 'bg-emerald-50 text-emerald-700 border-b-2 border-emerald-500'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                <Gift className="w-5 h-5" />
                One-Time Gift
              </button>
              <button
                onClick={() => setDonationType('monthly')}
                className={`flex-1 py-4 px-6 font-medium transition-colors flex items-center justify-center gap-2 ${
                  donationType === 'monthly'
                    ? 'bg-emerald-50 text-emerald-700 border-b-2 border-emerald-500'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                <Calendar className="w-5 h-5" />
                Monthly Support
              </button>
            </div>

            <div className="p-8">
              {donationType === 'monthly' && (
                <div className="mb-6 p-4 bg-amber-50 rounded-xl border border-amber-200">
                  <div className="flex items-center gap-2 text-amber-700 mb-2">
                    <Sparkles className="w-5 h-5" />
                    <span className="font-medium">Become a Monthly Partner</span>
                  </div>
                  <p className="text-sm text-amber-600">
                    Monthly donors provide sustainable support that helps us plan ahead and reach more children consistently.
                  </p>
                </div>
              )}

              {/* Amount Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 mb-3">
                  Select an amount
                </label>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                  {amounts.map((amount) => (
                    <button
                      key={amount}
                      onClick={() => {
                        setSelectedAmount(amount);
                        setCustomAmount('');
                      }}
                      className={`py-3 px-4 rounded-xl font-semibold transition-all ${
                        selectedAmount === amount
                          ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/30 scale-105'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                    >
                      £{amount}
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Amount */}
              <div className="mb-8">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Or enter a custom amount
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-medium">£</span>
                  <Input
                    type="number"
                    min="1"
                    step="1"
                    placeholder="Other amount"
                    value={customAmount}
                    onChange={(e) => {
                      setCustomAmount(e.target.value);
                      setSelectedAmount(null);
                    }}
                    className="pl-8 h-12 text-lg"
                  />
                </div>
              </div>

              {/* Summary */}
              {(selectedAmount || customAmount) && (
                <div className="mb-6 p-4 bg-slate-50 rounded-xl">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">
                      {donationType === 'monthly' ? 'Monthly donation' : 'One-time donation'}
                    </span>
                    <span className="text-2xl font-bold text-emerald-600">
                      £{selectedAmount || customAmount}
                      {donationType === 'monthly' && <span className="text-sm font-normal text-slate-500">/month</span>}
                    </span>
                  </div>
                </div>
              )}

              {/* Donate Button */}
              <Button
                onClick={handleDonate}
                disabled={loading || (!selectedAmount && !customAmount)}
                className="w-full h-14 bg-coral-500 hover:bg-coral-600 text-white text-lg font-semibold rounded-xl shadow-lg shadow-coral-500/30 transition-all hover:shadow-xl disabled:opacity-50"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                ) : (
                  <CreditCard className="w-5 h-5 mr-2" />
                )}
                {loading ? 'Processing...' : `Donate ${selectedAmount || customAmount ? `£${selectedAmount || customAmount}` : ''}`}
              </Button>

              <p className="text-center text-sm text-slate-500 mt-4">
                Secure payment powered by Stripe. You'll receive a receipt via email.
              </p>
            </div>
          </div>
        </section>

        {/* Impact Section */}
        <section className="max-w-4xl mx-auto px-4 mt-16">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-serif font-bold text-slate-800 mb-2">Your Impact</h2>
            <p className="text-slate-600">See how your donation helps children grow in faith</p>
          </div>
          
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {impactItems.map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                    <span className="text-emerald-600 font-bold">{item.amount}</span>
                  </div>
                  <Check className="w-5 h-5 text-emerald-500" />
                </div>
                <p className="text-slate-700">{item.impact}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Why Give Section */}
        <section className="max-w-4xl mx-auto px-4 mt-16">
          <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-3xl p-8 lg:p-12 text-white">
            <div className="flex items-center gap-3 mb-6">
              <TreePine className="w-8 h-8" />
              <h2 className="text-2xl font-serif font-bold">Why Give to Creassion?</h2>
            </div>
            
            <div className="grid gap-6 md:grid-cols-2">
              <div>
                <h3 className="font-semibold mb-2">100% Goes to the Mission</h3>
                <p className="text-emerald-100 text-sm">
                  As a volunteer-led charity, all donations directly support creating and distributing resources to children.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Faith-Centered Resources</h3>
                <p className="text-emerald-100 text-sm">
                  We create biblically accurate, engaging content that helps children understand and embrace their faith.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Reaching Underserved Communities</h3>
                <p className="text-emerald-100 text-sm">
                  We prioritize providing free resources to schools and families who cannot afford them.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Building the Next Generation</h3>
                <p className="text-emerald-100 text-sm">
                  Every child reached today is a future leader in their community and church.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Other Ways to Help */}
        <section className="max-w-4xl mx-auto px-4 mt-16 text-center">
          <h2 className="text-2xl font-serif font-bold text-slate-800 mb-4">Other Ways to Support</h2>
          <p className="text-slate-600 mb-6">
            Can't donate right now? There are other meaningful ways to help our mission.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button variant="outline" className="rounded-full" onClick={() => window.location.href = '/join-team'}>
              Volunteer With Us
            </Button>
            <Button variant="outline" className="rounded-full" onClick={() => {
              if (navigator.share) {
                navigator.share({ title: 'Creassion Resources', url: window.location.href });
              }
            }}>
              Share Our Mission
            </Button>
            <Button variant="outline" className="rounded-full" onClick={() => window.location.href = '/contact'}>
              Partner With Us
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default DonatePage;
